import { useState, useEffect, useRef } from 'react'
import { ListImperativeAPI } from 'react-window'
import { ListItemType } from '../types'

/**
 * useStickyHeader — encapsulates scroll-based sticky org header computation.
 *
 * Attaches a native scroll event listener to the VirtualList's outer element
 * (via listRef.current.element), walks cumulative item heights to determine
 * which org header should be "stuck" at the top, and cleans up on unmount.
 *
 * Only active on the 'all' tab — returns null on 'pinned'.
 *
 * Uses refs for frequently-changing values (listItems, activeTab, getItemSize)
 * so the scroll listener is registered once per open and never torn down/re-attached
 * mid-scroll, avoiding stutter.
 */
export function useStickyHeader(
    listRef: React.RefObject<ListImperativeAPI | null>,
    listItems: ListItemType[],
    activeTab: 'all' | 'pinned',
    getItemSize: (index: number) => number,
    visible: boolean,
): {
    stickyOrgHeader: { accountId: number; accountName: string; canAccess: boolean } | null
} {
    const [stickyOrgHeader, setStickyOrgHeader] = useState<{
        accountId: number
        accountName: string
        canAccess: boolean
    } | null>(null)

    // Refs so the scroll handler always reads latest values without re-registering
    const listItemsRef = useRef(listItems)
    listItemsRef.current = listItems

    const activeTabRef = useRef(activeTab)
    activeTabRef.current = activeTab

    const getItemSizeRef = useRef(getItemSize)
    getItemSizeRef.current = getItemSize

    // Reset on tab change
    useEffect(() => {
        setStickyOrgHeader(null)
    }, [activeTab])

    // Reset on visibility change
    useEffect(() => {
        if (!visible) {
            setStickyOrgHeader(null)
        }
    }, [visible])

    // Recompute sticky header when listItems change (e.g. search filtering)
    useEffect(() => {
        if (!visible || activeTab === 'pinned') return

        const el = listRef.current?.element
        const scrollTop = el?.scrollTop ?? 0
        const items = listItems
        const sizer = getItemSize

        let cumulativeHeight = 0
        let lastOrgHeader: { accountId: number; accountName: string; canAccess: boolean } | null = null

        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (cumulativeHeight > scrollTop + 1) {
                break
            }
            if (item.type === 'org-header') {
                lastOrgHeader = {
                    accountId: item.accountId,
                    accountName: item.accountName,
                    canAccess: item.canAccess,
                }
            }
            cumulativeHeight += sizer(i)
        }

        setStickyOrgHeader(lastOrgHeader)
    }, [listItems, visible, activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

    // Single scroll listener — registered once when popover opens, cleaned up when it closes
    useEffect(() => {
        if (!visible) return

        const timer = setTimeout(() => {
            const el = listRef.current?.element
            if (!el) return

            const onScroll = () => {
                if (activeTabRef.current === 'pinned') {
                    setStickyOrgHeader(null)
                    return
                }

                const items = listItemsRef.current
                const sizer = getItemSizeRef.current
                const scrollTop = el.scrollTop

                let cumulativeHeight = 0
                let lastOrgHeader: { accountId: number; accountName: string; canAccess: boolean } | null = null

                for (let i = 0; i < items.length; i++) {
                    const item = items[i]
                    if (cumulativeHeight > scrollTop + 1) {
                        break
                    }
                    if (item.type === 'org-header') {
                        lastOrgHeader = {
                            accountId: item.accountId,
                            accountName: item.accountName,
                            canAccess: item.canAccess,
                        }
                    }
                    cumulativeHeight += sizer(i)
                }

                setStickyOrgHeader(lastOrgHeader)
            }

            el.addEventListener('scroll', onScroll, { passive: true })

            // Fire once immediately so the header is correct on open
            onScroll()

            // Cleanup stored for effect teardown
            cleanupRef.current = () => {
                el.removeEventListener('scroll', onScroll)
            }
        }, 50)

        const cleanupRef = { current: null as (() => void) | null }

        return () => {
            clearTimeout(timer)
            cleanupRef.current?.()
        }
    }, [visible, listRef]) // only re-run when visibility or the ref itself changes

    return {
        stickyOrgHeader,
    }
}
